import { FunctionComponent, useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import Certification from './CertificationCard';
import { css } from '@compiled/react';
import MainPanelSectionHeader from 'components/common/section/MainPanelSectionHeader';
import { Link, usePathParam } from 'react-resource-router';
import { token } from '@atlaskit/tokens';
import { AtlassianAccountAnyUserQuery } from 'graphql/__generated__/AtlassianAccountAnyUserQuery.graphql';
import { ATLASSIAN_ACCOUNT_ANY_USER_QUERY } from 'graphql/AtlassianAccountAnyUserQuery';
import { ProfileHeaderQuery } from 'graphql/__generated__/ProfileHeaderQuery.graphql';
import { PROFILE_HEADER_QUERY } from 'graphql/ProfileHeaderQuery';
import useCertmetricsCertificates from 'utils/useCertmetricsCertificates';
import { CERTIFICATE_STATUS, SCREEN_NAME } from 'constants/index';
import { MOBILE, DESKTOP } from '../../../../constants/breakpoints';
import { sendScreenEvent, sendUIEvent } from 'services/analytics';
import useIsPersonalView from 'utils/useIsPersonalView';

type CertificationsFullListProps = {
  internalId: string;
  accountId: string;
};

const CertificationsFullList: FunctionComponent<
  CertificationsFullListProps
> = ({ internalId, accountId }) => {
  const { certificates } = useCertmetricsCertificates({
    internalId,
    status: CERTIFICATE_STATUS.ACTIVE,
    type: ['CERTIFICATION'],
    sortField: 'ACTIVE_DATE',
    sortDirection: 'DESC',
  });

  const userResponseData = useLazyLoadQuery<AtlassianAccountAnyUserQuery>(
    ATLASSIAN_ACCOUNT_ANY_USER_QUERY,
    { accountId },
    { fetchPolicy: 'store-or-network' },
  );
  const userData = userResponseData.user;

  const isPersonalView = useIsPersonalView(internalId);

  useEffect(() => {
    if (certificates) {
      sendScreenEvent({
        screenName: SCREEN_NAME.CERTIFICATIONS,
        isPersonalView,
      });
    }
  }, [certificates]);

  const onClickBackToProfile = () => {
    sendUIEvent({
      source: SCREEN_NAME.CERTIFICATIONS,
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'backToProfile',
      isPersonalView: true,
    });
  };

  const BackToProfileButton = () => {
    return (
      <Link
        to={`/profile/${internalId}`}
        onClick={onClickBackToProfile}
        css={styles.backLink}
      >
        {'← Back to profile'}
      </Link>
    );
  };

  if (!certificates) {
    return null;
  }

  return (
    <div css={styles.wrapper}>
      <div css={styles.contentWrapper}>
        <div css={styles.container}>
          <BackToProfileButton />
          <h2 css={styles.title}>{`${userData?.nickname}'s certifications`}</h2>
          <MainPanelSectionHeader
            testId="certifications-full-list-title"
            title={`${certificates.length} ${
              certificates.length === 1 ? 'Certification' : 'Certifications'
            }`}
          />
        </div>
        <div
          data-testid="certifications-full-list"
          css={styles.certificationContainer}
        >
          {certificates?.length > 0
            ? certificates.map((cert, i) => (
                <div css={styles.certificationItem} key={i}>
                  <Certification
                    testId={`certification-card-${i}`}
                    isPersonalView={isPersonalView}
                    data={{
                      name: cert?.name ?? null,
                      imageUrl: cert?.imageUrl ?? null,
                      publicUrl: cert?.imageUrl ?? null, // temporary fix
                      nameAbbr: cert?.nameAbbr ?? null,
                    }}
                  />
                </div>
              ))
            : null}
        </div>
      </div>
    </div>
  );
};

const CertificationsFullListProfileContainer = () => {
  const [pathProfileId] = usePathParam('pathProfileId') as unknown as string;

  const profileResponseData = useLazyLoadQuery<ProfileHeaderQuery>(
    PROFILE_HEADER_QUERY,
    { internalId: pathProfileId as string },
    { fetchPolicy: 'store-or-network' },
  );
  const profileData = profileResponseData?.unified?.unifiedProfile;

  if (!profileData?.aaid) {
    return <div>{'Could not fetch profile data'}</div>;
  }

  return (
    <CertificationsFullList
      internalId={pathProfileId}
      accountId={profileData.aaid}
    />
  );
};

const styles = {
  wrapper: css({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      paddingBottom: '32px',
    },
  }),
  contentWrapper: css({
    display: 'flex',
    flexDirection: 'column',
    marginTop: token('space.500'),
    alignItems: 'center',
    width: '1020px',
    maxWidth: '100%',
    [`@media (min-width: ${MOBILE.MAX_WIDTH}px) and (max-width: ${DESKTOP.MIN_WIDTH}px)`]:
      {
        width: '720px',
      },
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      marginTop: token('space.400'),
      width: '343px',
    },
  }),
  container: css({
    width: '1020px',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [`@media (min-width: ${MOBILE.MAX_WIDTH}px) and (max-width: ${DESKTOP.MIN_WIDTH}px)`]:
      {
        width: '720px',
      },
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      width: '343px',
    },
  }),
  title: css({
    fontSize: token('space.500'),
    fontWeight: token('font.weight.bold'),
    fontFamily: token('font.family.brand.heading'),
    lineHeight: token('space.600'),
    marginTop: '40px',
    paddingBottom: '40px',
    borderBottom: `1px solid ${token('color.border')}`,
    marginBottom: '40px',
    width: '100%',
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      paddingBottom: '24px',
      marginBottom: '24px',
      marginTop: '24px',
      fontSize: token('space.400'),
    },
  }),
  certificationContainer: css({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px',
    marginTop: '24px',
    width: '100%',

    [`@media (min-width: ${MOBILE.MAX_WIDTH}px) and (max-width: ${DESKTOP.MIN_WIDTH}px)`]:
      {
        gridTemplateColumns: 'repeat(2, 1fr)',
      },
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      gridTemplateColumns: '1fr',
      gap: token('space.200'),
      marginTop: '16px',
    },
  }),
  certificationItem: css({
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '348px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
      maxWidth: '343px',
    },
  }),
  backLink: css({
    alignSelf: 'flex-start',
    color: token('color.text.brand'),
    fontFamily: token('font.family.brand.body'),
    fontSize: token('space.200'),
    fontWeight: token('font.weight.semibold'),
    lineHeight: token('space.250'),
  }),
};

export default CertificationsFullListProfileContainer;
